<div class="feedback-config">
  <div class="feedback-config__header">
    <div class="feedback-config__intro">
      <h2 class="feedback-config__title">Feedback by location</h2>
      <p class="feedback-config__description">
        Turn the feedback form on for the locations that should send it. Customize the SMS message,
        Google review URL, and send delay for each location.
      </p>
    </div>
    <button type="button" class="feedback-config__add-btn" (click)="openAddModal()">
      <span aria-hidden="true">+</span>
      Add feedback configuration
    </button>
  </div>

  <div class="feedback-config__state" *ngIf="loading">Loading feedback configurations…</div>
  <div
    class="feedback-config__state feedback-config__state--error"
    *ngIf="!loading && errorMessage"
  >
    {{ errorMessage }}
  </div>

  <div class="feedback-config__table-wrap" *ngIf="!loading && !errorMessage">
    <div class="feedback-config__table-head">
      <div class="feedback-config__location-head">Location</div>
      <div>SMS Message</div>
      <div>Google Review URL</div>
      <div>Send Timing</div>
      <div class="feedback-config__actions-head">Actions</div>
    </div>

    <div class="feedback-config__row" *ngFor="let row of configuredRows">
      <div class="feedback-config__location">
        <div class="feedback-config__location-name" [title]="row.branch || row.id">
          {{ row.branch || row.id }}
        </div>
      </div>
      <div class="feedback-config__message" [title]="messagePreview(row.feedback_sms_message)">
        {{ messagePreview(row.feedback_sms_message) }}
      </div>
      <div class="feedback-config__url" [title]="row.google_review_url || ''">
        {{ row.google_review_url }}
      </div>
      <div class="feedback-config__timing">{{ timingLabel(row.feedback_sms_delay_minutes) }}</div>
      <div class="feedback-config__actions">
        <button
          type="button"
          class="feedback-config__menu-trigger"
          [class.is-open]="openMenuLocationId === row.id"
          [attr.aria-label]="'Actions for ' + (row.branch || row.id)"
          (click)="toggleMenu(row.id, $event)"
        >
          <span aria-hidden="true">⋮</span>
        </button>
        <div
          class="feedback-config__menu"
          *ngIf="openMenuLocationId === row.id"
          (click)="$event.stopPropagation()"
        >
          <button type="button" (click)="openEditModal(row, $event)">Edit</button>
          <button type="button" class="is-danger" (click)="disableConfiguration(row, $event)">
            Disable
          </button>
        </div>
      </div>
    </div>

    <div class="feedback-config__empty" *ngIf="configuredRows.length === 0">
      <div class="feedback-config__empty-title">No locations are sending feedback yet</div>
      <div class="feedback-config__empty-copy">
        Add a configuration to switch the feedback form on for one or more locations.
      </div>
    </div>
  </div>
</div>

<div #modalOverlay class="feedback-config-modal" *ngIf="modalOpen" (click)="closeModal()">
  <div
    class="feedback-config-modal__panel"
    role="dialog"
    aria-modal="true"
    (click)="$event.stopPropagation()"
  >
    <div class="feedback-config-modal__header">
      <div>
        <h3>{{ modalTitle }}</h3>
        <p>{{ modalSubtitle }}</p>
      </div>
      <button
        type="button"
        class="feedback-config-modal__close"
        aria-label="Close"
        (click)="closeModal()"
      >
        ×
      </button>
    </div>

    <div class="feedback-config-modal__body">
      <div class="feedback-config-modal__field" *ngIf="showLocationPicker">
        <div class="feedback-config-modal__field-head">
          <label>Locations</label>
          <button
            type="button"
            class="feedback-config-modal__link"
            (click)="toggleSelectAll($event)"
          >
            {{ toggleAllLabel }}
          </button>
        </div>
        <button type="button" class="feedback-config-modal__picker" (click)="togglePicker($event)">
          <span>{{ pickerLabel }}</span>
          <span aria-hidden="true">{{ pickerOpen ? '▴' : '▾' }}</span>
        </button>
        <div
          class="feedback-config-modal__picker-list"
          *ngIf="pickerOpen"
          (click)="$event.stopPropagation()"
        >
          <button
            type="button"
            class="feedback-config-modal__picker-option"
            *ngFor="let row of locations"
            (click)="toggleLocationSelection(row.id, $event)"
          >
            <span
              class="feedback-config-modal__checkbox"
              [class.is-checked]="isLocationSelected(row.id)"
            ></span>
            <span class="feedback-config-modal__picker-text">
              <span>{{ row.branch || row.id }}</span>
              <small *ngIf="isLocationConfigured(row.id)">Configured</small>
            </span>
          </button>
        </div>
        <p class="feedback-config-modal__hint" *ngIf="selectedLocationIds.length">
          {{ selectedLocationIds.length }} location{{ selectedLocationIds.length === 1 ? '' : 's' }}
          selected
        </p>
      </div>

      <div class="feedback-config-modal__field" *ngIf="modalMode === 'edit'">
        <label>Location</label>
        <div class="feedback-config-modal__readonly">{{ locationLabel(editingLocationId) }}</div>
      </div>

      <div class="feedback-config-modal__field">
        <label for="feedback-google-url">Google Review URL</label>
        <input
          id="feedback-google-url"
          type="url"
          class="feedback-config-modal__input"
          [class.is-error]="urlError"
          [ngModel]="draftUrl"
          (ngModelChange)="onDraftUrlChange($event)"
          placeholder="https://g.page/r/…/review"
          [disabled]="saving"
        />
        <p class="feedback-config-modal__error" *ngIf="urlError">
          Enter a full URL starting with https:// — for example https://g.page/r/abc123/review
        </p>
      </div>

      <div class="feedback-config-modal__field">
        <label for="feedback-send-timing">Send timing</label>
        <p class="feedback-config-modal__help">
          How long to wait after the customer's first redemption before the feedback SMS goes out.
        </p>
        <div class="feedback-config-modal__minutes">
          <input
            id="feedback-send-timing"
            type="number"
            min="1"
            step="1"
            class="feedback-config-modal__input feedback-config-modal__input--minutes"
            [ngModel]="draftMinutes"
            (ngModelChange)="onDraftMinutesChange($event)"
            [disabled]="saving"
          />
          <span>minutes</span>
        </div>
        <p class="feedback-config-modal__error" *ngIf="minutesError">
          Enter a positive whole number of minutes.
        </p>
      </div>

      <div class="feedback-config-modal__preview">
        <label for="feedback-sms-message">Message preview</label>
        <p class="feedback-config-modal__help">
          Edit the SMS intro text. The feedback link is appended automatically when the message is
          sent.
        </p>
        <textarea
          id="feedback-sms-message"
          rows="3"
          class="feedback-config-modal__preview-text"
          [class.is-error]="messageError"
          [ngModel]="draftMessage"
          (ngModelChange)="onDraftMessageChange($event)"
          [disabled]="saving"
          [attr.maxlength]="320"
        ></textarea>
        <p class="feedback-config-modal__error" *ngIf="messageError">
          Enter a message up to 320 characters.
        </p>
        <code [class.is-error]="!isFeedbackPublicOriginConfigured">{{ previewFeedbackUrl }}</code>
      </div>
    </div>

    <div class="feedback-config-modal__footer">
      <button
        type="button"
        class="feedback-config-modal__secondary"
        (click)="closeModal()"
        [disabled]="saving"
      >
        Cancel
      </button>
      <button
        type="button"
        class="feedback-config-modal__primary"
        [disabled]="!canSave"
        (click)="saveModal()"
      >
        {{ saving ? 'Saving…' : 'Save configuration' }}
      </button>
    </div>
  </div>
</div>
